import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { 
  Truck, 
  Users, 
  TrendingUp, 
  Ship,
  BarChart3,
  Loader
} from 'lucide-react';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 60px;
  height: 60px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ $color }) => $color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.xl};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const WelcomeCard = styled(Card)`
  background: linear-gradient(135deg, #008752 0%, #006B41 100%);
  color: white;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const WelcomeTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const WelcomeText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  opacity: 0.9;
  margin: 0;
`;

const QuickActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const QuickAction = styled.a`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const QuickActionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`;

const QuickActionText = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

interface Estadisticas {
  total_rutas: number;
  total_proveedores: number;
  total_propuestas: number;
  proveedores_con_propuestas: number;
  tasa_conversion: number;
}

const Dashboard: React.FC = () => {
  const { proveedor } = useAuth();
  const [estadisticas, setEstadisticas] = useState<Estadisticas | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarEstadisticas = async () => {
      try {
        const response = await api.get('/admin/estadisticas');
        setEstadisticas(response.data);
      } catch (error) {
        console.error('Error cargando estadísticas:', error);
      } finally {
        setLoading(false);
      }
    };

    cargarEstadisticas();
  }, []);

  return (
    <Container>
      <WelcomeCard>
        <WelcomeTitle>
          ¡Bienvenido, {proveedor?.razon_social || proveedor?.username || 'Administrador'}!
        </WelcomeTitle>
        <WelcomeText>
          Panel de administración de COMEX - Gestión de Rutas y Proveedores
        </WelcomeText>
      </WelcomeCard>

      <StatsGrid>
        <StatCard>
          <StatIcon $color="#008752">
            <Truck size={28} />
          </StatIcon>
          <StatContent>
            <StatValue>{loading ? <Loader size={20} /> : estadisticas?.total_rutas || 0}</StatValue>
            <StatLabel>Rutas Activas</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="#10b981">
            <Users size={28} />
          </StatIcon>
          <StatContent>
            <StatValue>{loading ? <Loader size={20} /> : estadisticas?.total_proveedores || 0}</StatValue>
            <StatLabel>Proveedores Registrados</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="#f59e0b">
            <Ship size={28} />
          </StatIcon>
          <StatContent>
            <StatValue>{loading ? <Loader size={20} /> : estadisticas?.total_propuestas || 0}</StatValue>
            <StatLabel>Propuestas Recibidas</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon $color="#8b5cf6">
            <TrendingUp size={28} />
          </StatIcon>
          <StatContent>
            <StatValue>{loading ? <Loader size={20} /> : estadisticas?.proveedores_con_propuestas || 0}</StatValue>
            <StatLabel>Proveedores con Propuestas</StatLabel>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <ContentGrid>
        <Card>
          <CardHeader>
            <CardTitle>
              <BarChart3 size={20} />
              Resumen de Actividad
            </CardTitle>
          </CardHeader>
          <div style={{ padding: '20px 0' }}>
            <p style={{ color: '#64748b', marginBottom: '10px' }}>
              <strong>Proveedores con propuestas:</strong> {estadisticas?.proveedores_con_propuestas || 0}
            </p>
            <p style={{ color: '#64748b' }}>
              <strong>Total de propuestas enviadas:</strong> {estadisticas?.total_propuestas || 0}
            </p>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Acciones Rápidas</CardTitle>
          </CardHeader>
          <QuickActionsList>
            <QuickAction href="/proveedores">
              <QuickActionIcon>
                <Users size={20} />
              </QuickActionIcon>
              <QuickActionText>Gestionar Proveedores</QuickActionText>
            </QuickAction>
          </QuickActionsList>
        </Card>
      </ContentGrid>
    </Container>
  );
};

export default Dashboard;

